/**
 * "Daily email summary" — the Settings card that turns on the once-a-day
 * email to the workspace's owners: how the ads did on the ad account's
 * previous day, whether Google is actually showing each placement, what needs
 * attention, and whether AdOpt's own jobs are running.
 *
 * Off until an owner or admin turns it on (the server decides `canEdit` and
 * refuses anyone else). Each email is billed to the workspace's billing
 * account at the usual email rate — said so here, because nothing is free.
 * Pessimistic: what is shown is what the server saved.
 */

import { useAdoptDailyDigest, usePutAdoptDailyDigest } from '../hooks/useAdvertisingOptimization';

export function DailyDigestCard({ holderId }: { holderId: string }) {
  const digestQuery = useAdoptDailyDigest(holderId);
  const put = usePutAdoptDailyDigest(holderId);
  const digest = digestQuery.data;
  if (!digest) return null;

  return (
    <section
      className="flex flex-col gap-2 rounded-lg border border-border p-4"
      data-testid="daily-digest-card"
    >
      <div className="flex items-center justify-between gap-3">
        <span className="text-sm font-medium">Daily email summary</span>
        <label className="flex items-center gap-2 text-sm">
          <input
            type="checkbox"
            checked={digest.enabled}
            // Turning it off always works; on only where it would send.
            disabled={!digest.canEdit || put.isPending || (!digest.enabled && !digest.available)}
            onChange={(e) => put.mutate(e.target.checked)}
          />
          {digest.enabled ? 'On' : 'Off'}
        </label>
      </div>
      <p className="text-xs text-muted-foreground">
        Every morning, the workspace&apos;s owners get one email about the ad account&apos;s
        previous day: spend, impressions and clicks, whether Google is actually showing each
        placement, what needs attention, and whether AdOpt&apos;s syncs and optimizer are running.
        Each email is billed to the workspace&apos;s billing account at the standard email rate (a
        small fraction of a cent per email).
      </p>
      {digest.lastSentDay && (
        <p className="text-xs text-muted-foreground">Last summary covered {digest.lastSentDay}.</p>
      )}
      {!digest.available && (
        <p className="text-xs text-amber-700">
          AdOpt is not open to this workspace&apos;s members yet, so no summary would be sent. A
          platform admin can open it from the admin console.
        </p>
      )}
      {!digest.canEdit && (
        <p className="text-xs text-muted-foreground">
          Only a workspace owner or admin can turn it on or off.
        </p>
      )}
      {put.isError && (
        <p className="text-xs text-rose-600" role="alert">
          {put.error instanceof Error ? put.error.message : 'Could not save'}
        </p>
      )}
    </section>
  );
}
